import { MapEmbed } from "@/components/MapEmbed";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { ContextLinkBlock } from "@/components/ContextLinkBlock";
import { SALON_SERVICE_LINKS } from "@/data/localServiceLinks";
import { BarberGrid } from "@/components/BarberGrid";
import { SALON_BARBERARE, salonBarberLine } from "@/data/barbers";
import { Button } from "@/components/ui/button";
import { salonBySlug } from "@/data/salons";
import { ReviewsBlock } from "@/components/ReviewsBlock";
import { breadcrumbSchema, faqSchema, jsonLd, localBusinessSchema, SITE_URL, absUrl } from "@/lib/seo";
import { reviewsForSalon } from "@/data/reviews";
import { formatCount, formatRating, ratingFor, reviewCountFor } from "@/data/reviewStats";
import { SalonRatingLines } from "@/components/SalonRatingLines";
import tabyUtanfor from "@/assets/opt/taby-utanfor.webp";
import tabyUtanfor640 from "@/assets/opt/taby-utanfor-640.webp";
import tabyUtanfor960 from "@/assets/opt/taby-utanfor-960.webp";
import tabyUtanfor1280 from "@/assets/opt/taby-utanfor-1280.webp";
import tabyInsida from "@/assets/opt/taby-insida.webp";
import tabyInsida400 from "@/assets/opt/taby-insida-400.webp";
import tabyInsida640 from "@/assets/opt/taby-insida-640.webp";
import tabyInsida900 from "@/assets/opt/taby-insida-900.webp";
import { withBookingUtm } from "@/lib/booking";
import { PRICE } from "@/data/pricing";

const utanforSrcSet = `${tabyUtanfor640} 640w, ${tabyUtanfor960} 960w, ${tabyUtanfor1280} 1280w, ${tabyUtanfor} 1600w`;
const insidaSrcSet = `${tabyInsida400} 400w, ${tabyInsida640} 640w, ${tabyInsida900} 900w, ${tabyInsida} 1200w`;

const salon = salonBySlug["taby"];
const BOOK_URL = withBookingUtm("https://www.bokadirekt.se/places/tailors-barber-taby-centrum-37677");
const MAPS_LINK = "https://maps.app.goo.gl/7egymx9yvWnbJs63A";
const MAPS_EMBED = `https://www.google.com/maps?q=${encodeURIComponent("Biblioteksgången 1 Täby")}&output=embed`;

const services = [
  ["Herrklippning", "450 kr"],
  ["Taper Fade / Skin Fade", "450 kr"],
  ["Skäggtrimning", "380 kr"],
  ["Herrklippning + Skäggtrimning", "680 kr"],
  ["Barnklippning (upp till 10 år)", "380 kr"],
  ["Seniorklippning (mån–tors 10–14)", "270 kr"],
];

const faq = [
  { q: "Var ligger Tailor's Barber i Täby Centrum?", a: "Vi finns i Westfield Täby Centrum, Biblioteksgången 1. Du hittar oss enkelt inne i köpcentrumet – följ skyltarna till herreavdelningen." },
  { q: "Måste jag boka i förväg i Täby?", a: "Onlinebokning rekommenderas starkt då vår Täby-salong är den mest bokade i regionen. Drop-in är välkommet i mån av tid – störst chans tidigt på morgonen vardagar." },
  { q: "Hur mycket kostar en herrklippning i Täby?", a: "Herrklippning kostar 450 kr. Taper fade och skin fade kostar också 450 kr. Kombination herrklippning + skägg 680 kr. Barnklippning (upp till 10 år) 380 kr." },
  { q: "Vilka tjänster erbjuder ni i Täby?", a: "Vi erbjuder herrklippning, taper fade, skin fade, skäggtrimning, hår- och skäggpaket, barnklippning och drop-in i mån av plats." },
  { q: "Hur kommer jag till Tailor's Barber Täby med buss eller tunnelbana?", a: "Täby Centrum nås med pendelbuss 670 och 676 från Danderyds sjukhus (tunnelbana röd linje). Med bil finns parkering direkt i Westfield Täby Centrum." },
  { q: "Kan jag boka samma barberare varje gång?", a: "Ja, via BokaDirekt kan du se lediga tider per barberare och alltid boka med din favorit. Vi rekommenderar att du väljer en fast barberare för bäst resultat." },
  { q: "Är ni öppna på söndagar i Täby?", a: "Ja, vi har öppet söndag 10:00–18:00 i Täby Centrum." },
];

// Omdömestal kommer alltid från reviewStats.ts — aldrig hårdkodade här.
const REVIEW_COUNT = formatCount(reviewCountFor("taby"));
const RATING = formatRating(ratingFor("taby"));

const localBiz = {
  ...localBusinessSchema(salon, reviewsForSalon(salon.slug)),
  description: `Tailor's Barbers flaggskeppssalong i Westfield Täby Centrum. Herrklippning, taper fade, skin fade och skäggtrimning. ${REVIEW_COUNT} omdömen, betyg ${RATING}.`,
  containedInPlace: {
    "@type": "ShoppingCenter",
    name: "Westfield Täby Centrum",
    address: "Biblioteksgången 1, Täby",
  },
  hasMap: MAPS_LINK,
  priceRange: "$$",
  telephone: "+468371156",
  parentOrganization: { "@type": "Organization", name: "Tailor's Barber", url: SITE_URL },
};

export const Route = createFileRoute("/barberare-stockholm-taby")({
  head: () => ({
    meta: [
      { title: "Barberare Täby Centrum: herrklippning 450 kr | Tailor's Barber" },
      { name: "description", content: `Barberare Täby Centrum i Westfield. Herrklippning 450 kr, skin fade, taper fade och skäggtrimning. ${REVIEW_COUNT} omdömen, drop-in välkommet. Boka online.` },
      { property: "og:title", content: "Barberare Täby Centrum: herrklippning 450 kr | Tailor's Barber" },
      { property: "og:description", content: `Herrklippning, skin fade, taper fade och skägg i Westfield Täby Centrum. ${REVIEW_COUNT} omdömen · ${RATING} ⭐.` },
      { property: "og:url", content: absUrl(`${SITE_URL}/barberare-stockholm-taby/`) },
      { property: "og:image", content: absUrl(tabyUtanfor) },
      { property: "og:image:alt", content: "Entrén till Tailor's Barber i Westfield Täby Centrum" },
      { property: "og:locale", content: "sv_SE" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absUrl(tabyUtanfor) },
    ],
    links: [
      { rel: "canonical", href: absUrl(`${SITE_URL}/barberare-stockholm-taby/`) },
      {
        rel: "preload",
        as: "image",
        href: tabyUtanfor960,
        imagesrcset: utanforSrcSet,
        imagesizes: "(max-width: 1024px) 62vw, 400px",
        fetchpriority: "high",
      },
    ],
    scripts: [
      jsonLd(localBiz),
      jsonLd(faqSchema(faq)),
      jsonLd(breadcrumbSchema([
        { name: "Hem", url: "/" },
        { name: "Täby Centrum", url: "/barberare-stockholm-taby/" },
      ])),
    ],
  }),
  component: TabyPage,
});

function TabyPage() {
  return (
    <SiteShell>
      <section className="container-page pt-6 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Täby Centrum</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="eyebrow">Barberare i Täby</div>
            <h1 className="mt-3 text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
              Barberare Täby Centrum – Tailor's Barber
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-prose leading-relaxed">
              Barberare Täby Centrum i Westfield är vår flaggskeppssalong, den mest bokade salongen i
              norra Stockholm. Vi finns i Westfield Täby Centrum med tillgång till hundratals
              parkeringsplatser och busshållplatser direkt utanför. Här jobbar regionens mest erfarna
               barberare, specialiserade på herrklippning, skin fade och skäggtrimning. Herrklippning {PRICE.herrklippning} kr, skäggtrimning {PRICE.skaggtrimning} kr, hår och skägg {PRICE.klippningSkagg} kr.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/70">
              <SalonRatingLines salon="taby" />
              <span>📍 Biblioteksgången 1, Täby</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <Button asChild size="lg">
                <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online</a>
              </Button>
              <span className="text-sm text-foreground/70">Drop-in välkommen i mån av tid</span>
            </div>
          </div>
          <div className="grid grid-cols-3 grid-rows-1 gap-3 aspect-[4/3]">
            <div className="col-span-2 rounded-2xl overflow-hidden bg-muted shadow-soft">
              <img
                src={tabyUtanfor960}
                srcSet={utanforSrcSet}
                sizes="(max-width: 1024px) 62vw, 400px"
                alt="Barberare Täby Centrum: entrén till Tailor's Barber i Westfield på Biblioteksgången 1"
                width={1600}
                height={900}
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden bg-muted shadow-soft">
              <img
                src={tabyInsida400}
                srcSet={insidaSrcSet}
                sizes="(max-width: 1024px) 31vw, 200px"
                alt="Barberare Täby Centrum interiör med två barberarstolar, speglar och produkthyllor"
                width={1200}
                height={1600}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="container-page py-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="font-medium">Öppettider Täby Centrum:</span>
          <span><strong>Mån–Fre</strong> 10:00–20:00</span>
          <span><strong>Lör</strong> 10:00–18:00</span>
          <span><strong>Sön</strong> 10:00–18:00</span>
        </div>
      </section>

      <section className="container-page py-12">
        <h2 className="text-2xl md:text-3xl">Täbys mest populära barbersalong</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Med över {REVIEW_COUNT} omdömen och ett snittbetyg på {RATING} stjärnor är Tailor's Barber Täby Centrum
          den i särklass mest bokade barbersalongen i norra Stockholmsregionen. Salongen erbjuder:
        </p>
        <ul className="mt-5 grid gap-3 md:grid-cols-2 max-w-3xl">
          <li className="card-elev p-5"><strong>Westfield Täby Centrum</strong> – inomhusläge med gratis parkering för hundratals bilar</li>
          <li className="card-elev p-5"><strong>Regionens mest erfarna barberare</strong> – specialiserade på fade-tekniker och precision</li>
          <li className="card-elev p-5"><strong>Öppet alla dagar</strong> – även kvällar, lördagar och söndagar</li>
          <li className="card-elev p-5"><strong>Boka per barberare</strong> – välj din favorit och boka direkt online</li>
        </ul>
      </section>



      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-5">Tjänster och priser i Täby</h2>
        <div className="card-elev overflow-hidden max-w-3xl">
          <table className="w-full text-left">
            <tbody>
              {services.map(([name, price], i) => (
                <tr key={i} className={i > 0 ? "border-t border-border/60" : ""}>
                  <td className="px-5 py-4">{name}</td>
                  <td className="px-5 py-4 text-right font-medium whitespace-nowrap">{price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          Se hela vår <Link to="/priser/" className="underline">prislista</Link>.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Våra specialiteter i Täby</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Vår Täby-salong är känd för precision i fadetekniker. Populärast bland våra kunder:
        </p>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li><Link to="/herrklippning/" className="underline">Herrklippning</Link> – 450 kr, ca 30–40 min</li>
          <li><Link to="/taper-fade/" className="underline">Taper Fade</Link> – gradvis övergång för ett rent, modernt uttryck</li>
          <li><Link to="/skin-fade-taby/" className="underline">Skin Fade Täby</Link> – vår mest bokade tjänst, razor-sharp precision</li>
          <li><Link to="/skaggtrimning/" className="underline">Skäggtrimning och skäggformning</Link> – 380 kr</li>
          <li><Link to="/herrklippning-skaggtrimning/" className="underline">Hår & Skägg-paket</Link> – komplett behandling, 680 kr</li>
          <li><Link to="/barnklippning-taby/" className="underline">Barnklippning Täby</Link> – för barn upp till 10 år, 380 kr</li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-6">Teamet på Täby Centrum</h2>
        <p className="mt-0 mb-6 text-foreground/80">{salonBarberLine("taby")}</p>
        <p className="mt-0 mb-8 text-foreground/80 max-w-3xl">
          Boka direkt med din favorit. Varje barberare har sin egen kalender på BokaDirekt –
          du väljer tid och person i samma bokning.
        </p>
        <BarberGrid barbers={SALON_BARBERARE["taby"].barbers} salonName="Täby Centrum" />
      </section>

      <ReviewsBlock salon="taby" heading="Vad våra kunder säger om Täby Centrum" />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Hitta till oss – Barberare i Täby Centrum</h2>
        <div className="grid md:grid-cols-2 gap-8 mt-6">
          <div>
            <p className="text-foreground/85">
              <strong>Adress:</strong>{" "}
              <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="underline">
                Biblioteksgången 1, 183 34 Täby (Westfield Täby Centrum)
              </a>
            </p>
            <h3 className="mt-5 text-lg font-medium">Kollektivtrafik</h3>
            <ul className="mt-2 space-y-1 text-foreground/80">
              <li><strong>Buss:</strong> Linje 670 och 676 stannar direkt vid Täby Centrum (från Danderyds sjukhus, röd linje)</li>
              <li><strong>Bil:</strong> Gratis parkering direkt i Westfield Täby Centrum</li>
            </ul>
            <h3 className="mt-5 text-lg font-medium">Öppettider</h3>
            <ul className="mt-2 space-y-1 text-foreground/80">
              <li><strong>Mån–Fre:</strong> 10:00–20:00</li>
              <li><strong>Lör:</strong> 10:00–18:00</li>
              <li><strong>Sön:</strong> 10:00–18:00</li>
            </ul>
          </div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted shadow-soft min-h-[280px]">
            <MapEmbed src={MAPS_EMBED} title="Karta över Tailor's Barber Täby Centrum" href={MAPS_LINK} />
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <div className="card-elev p-8 md:p-12 bg-ink text-cream">
          <div className="eyebrow text-brass">Boka eller drop-in</div>
          <h2 className="mt-3 text-3xl md:text-4xl text-cream">Redo att boka i Täby?</h2>
          <p className="mt-4 max-w-prose text-cream/80">
            Boka online – välj tid och barberare som passar dig. Drop-in välkommen i mån av plats,
            störst chans tidigt på förmiddagen vardagar. Så fungerar <Link to="/drop-in-barber-stockholm/" className="underline">drop-in hos barberare i Stockholm</Link>.
          </p>
          <div className="mt-6">
            <Button asChild size="lg" className="bg-brass text-ink hover:bg-brass/90">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online – Täby</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Barberare eller frisör i Täby?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Söker du frisör i Täby Centrum för en herrklippning hamnar du ofta i valet mellan en frisörsalong
          och en barbershop. Skillnaden ligger i vad vi gör mest av. En frisör arbetar brett – dam och herr,
          färg, längre frisyrer. Vi arbetar enbart med herrhår och skägg, och det är i det dagliga
          repetitiva som precisionen sitter: fades, linjering, nacklinjer och skäggformning.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Praktiskt betyder det att vi klipper mer med maskin och rakkniv och lägger mer tid på övergångar
          än på längd. För kort och medellångt herrhår, fade eller skägg är barbershopen därför oftast rätt
          val. Ska håret färgas eller växa ut till en längre frisyr rekommenderar vi hellre en frisör i
          Täby Centrum – det är inte vår specialitet.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Fler Tailor's Barber-salonger</h2>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li><Link to="/barberare-stockholm-arninge/" className="underline">Arninge Centrum</Link> – ICA Maxi Arninge, Täby</li>
          <li><Link to="/barberare-stockholm-akersberga/" className="underline">Åkersberga</Link> – ICA Maxi Åkersberga</li>
          <li><Link to="/barberare-stockholm-ahlens-city/" className="underline">Åhléns City Stockholm</Link> – 1 minut från T-Centralen</li>
          <li><Link to="/" className="underline">Alla salonger</Link></li>
        </ul>
      </section>

      <ContextLinkBlock
        heading="Tjänster i Täby"
        links={SALON_SERVICE_LINKS["taby"]!.links}
      />

      <Faq items={faq} heading="Vanliga frågor – Barberare Täby" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Boka barberare i Täby Centrum</h2>
          <p className="mt-3 text-foreground/80">Flaggskeppssalongen. Regionens mest erfarna barberare.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka online</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
